import React, { FC, useEffect, useState } from "react";
import {
  Box,
  HStack,
  Image,
  Pressable,
  Text,
  Tooltip,
  VStack,
} from "native-base";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as AV from "../../AVCore/AVCore";
import Moment from "moment";
import {
  CalenderCardIcon,
  LocationCardIcon,
  TimerCardIcon,
  Event,
} from "../../assets/icons/icon-assets";
import _ from "lodash";
import { Loc } from "../../App/Loc";
import RenderFilters from "../../components/Events/RenderFilters";
import { useWindowDimension } from "../../Utility/WindowDimensions";

interface Props {
  seriesEventsList: any | undefined;
  event: any | undefined;
  setEntity: any | undefined;
  setEntityDatas: any | undefined;
}

const SeriesEvents: FC<Props> = ({
  seriesEventsList,
  event,
  setEntity,
  setEntityDatas,
}) => {
  const [parentEventData, setParentEventData] = useState<AV.IEvent | null>(
    event,
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(
    (state: RootStateOrAny) => state.user.user[0],
  );

  // Filter options
  const [categories, setCategories] = useState<any>([]);
  const [selectedFilter, setSelectedFilter] = useState("Current");
  const [filteredEvents, setFilteredEvents] = useState<any>([]);
  const dimensions = useWindowDimension();
  const isMobile = dimensions?.width <= 500 || false;

  const isCanceled = (event: any) =>
    event?.data?.toLowerCase() === "canceled" ||
    event?.status?.toLowerCase() === "canceled";

  useEffect(() => {
    const now = new Date();
    const sortByDate = (a, b) =>
      new Date(a.start).getTime() - new Date(b.start).getTime();
    const currentEvt =
      currentUser?.events?.find((x) => x.id === event.id) ?? {};
    const filteredSeriesEvents =
      event?.seriesEvents?.filter((seriesEvent) =>
        currentEvt?.events?.some((e) => e.id === seriesEvent.id),
      ) ?? [];
    event.seriesEvents = filteredSeriesEvents;

    if (event?.status?.toLowerCase() !== "canceled") {
      const currentEvents = event?.seriesEvents
        ?.filter((ev) => !isCanceled(ev) && new Date(ev.end) >= now)
        .sort(sortByDate);

      const past = event?.seriesEvents
        ?.filter((ev) => new Date(ev.end) < now && !isCanceled(ev))
        .sort(sortByDate);

      const canceled = event?.seriesEvents
        ?.filter((ev) => isCanceled(ev))
        .sort(sortByDate);

      const uniqueCategories: string[] = [];

      if (currentEvents?.length > 0) uniqueCategories.push("Current");
      if (past?.length > 0) uniqueCategories.push("Past");
      if (canceled?.length > 0) uniqueCategories.push("Canceled");

      setCategories(uniqueCategories);

      setSelectedFilter(
        currentEvents?.length > 0
          ? "Current"
          : past?.length > 0
            ? "Past"
            : "Canceled",
      );

      setFilteredEvents(
        currentEvents?.length > 0
          ? currentEvents
          : past?.length > 0
            ? past
            : canceled,
      );
    } else {
      setCategories(["Canceled"]);
      setSelectedFilter("Canceled");
      setFilteredEvents(event?.seriesEvents?.sort(sortByDate));
    }
  }, [event]);

  const handleFilters = (filter) => {
    try {
      setSelectedFilter(filter);
      setFilteredEvents([]);

      const now = new Date();
      let filtered = [];
      const currentEvt =
        currentUser?.events?.find((x) => x.id === event.id) ?? {};
      const filteredSeriesEvents =
        event?.seriesEvents?.filter((seriesEvent) =>
          currentEvt?.events?.some((e) => e.id === seriesEvent.id),
        ) ?? [];
      event.seriesEvents = filteredSeriesEvents;

      if (event?.status?.toLowerCase() !== "canceled") {
        switch (filter) {
          case "Past":
            filtered = event?.seriesEvents?.filter(
              (ev) => new Date(ev.end) < now && !isCanceled(ev),
            );
            break;
          case "Canceled":
            filtered = event?.seriesEvents?.filter((ev) => isCanceled(ev));
            break;
          case "Current":
          default:
            filtered = event?.seriesEvents?.filter(
              (ev) => !isCanceled(ev) && new Date(ev.end) >= now,
            );
            break;
        }
      } else {
        filtered = event?.seriesEvents ?? [];
      }

      setFilteredEvents(filtered);
    } catch (e) {
      console.error("Error filtering events", e);
    }
  };

  const navigateToEvent = async (item) => {
    try {
      navigate(`/Events/${event?.id}/${event?.region}/${item?.id}`, {
        state: { team: event, series: item },
      });
    } catch (e) {
      console.log(e);
    }

    let isSeries = event.seriesEvents.find((x) => x.id == event.id);
    if (isSeries) {
      let fk = {
        id: event.series,
        pk: event.series.slice(-4),
        region: event.region,
        type: event.type,
      };
      event = await AV.AVEntity.get(fk);
    }

    event.start = item.start;
    event.end = item.end;
    event.locURL = item.locURL ? item.locURL : parentEventData?.locURL;
    event.participants = _.uniqBy(
      [...(parentEventData?.participants || []), ...(item.participants || [])],
      "id",
    );
    event.resources = _.uniqBy(
      [...(parentEventData?.resources || []), ...(item.resources || [])],
      "resourceId",
    );
    event.status = item.status ? item.status : parentEventData?.status;
  };

  return (
    <VStack p={1} zIndex={-3} width={"100%"}>
      {/* Filter Bar */}
      <div className="custom-scrollbar">
        <HStack
          marginY={"24px"}
          overflowY={isMobile ? "auto" : undefined}
          backgroundColor={"rgba(0,0,0,0.05)"}
          borderRadius={"18px"}
          marginLeft={"24px"}
          height={"36px"}
          width={"fit-content"}
        >
          <RenderFilters
            categories={categories}
            selectedFilter={selectedFilter}
            handleFilters={handleFilters}
          />
        </HStack>
      </div>

      <HStack alignItems={"center"} px={5} flexWrap={"nowrap"} space={2}>
        <HStack flexWrap={"wrap"} maxWidth={"100%"} alignSelf={"flex-start"}>
          {filteredEvents?.map((item, i) => {
            let participant = event?.participants?.find(
              (x) => x.id == currentUser.id,
            );
            let profileImg = event?.thumb
              ? event?.thumb
              : event?.profile?.pic?.id;
            let backgroundImg = event?.background
              ? event.background
              : event?.profile?.background?.id;
            let profileImage = "";
            let backgroundImage = "";
            let thumb: any = AV.AVEntity.getLink(event);
            if (thumb) profileImage = AV.Media.getThumb(thumb);
            else if (item?.profile?.pic?.id)
              profileImage = AV.Media.getThumb(AV.AVEntity.getLink(item));
            if (item?.background)
              backgroundImage = AV.Media.getBackground(item);
            else if (item?.profile?.background?.id)
              backgroundImage = AV.Media.getBackground(
                AV.AVEntity.getLink(item),
              );
            else if (event?.profile?.background?.id)
              backgroundImage = AV.Media.getBackground(
                AV.AVEntity.getLink(event),
              );

            return (
              <Pressable
                key={item?.id || i}
                width={"235px"}
                marginY={"12px"}
                marginX={"5px"}
                bgColor={"rgba(0,167,188,0.11)"}
                borderRadius={6}
                borderColor={"AVColor.bgcolor"}
                shadow={1}
                overflow={"hidden"}
                _hover={{
                  shadow: 3,
                }}
                onPress={() => navigateToEvent(item)}
              >
                {item?.title}

                <Box
                  width={"100%"}
                  height={"69px"}
                  bgColor={"rgba(0,167,188,0.26)"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  {backgroundImg && (
                    <Image
                      source={{
                        uri: backgroundImage,
                      }}
                      width={"100%"}
                      height={"100%"}
                      position={"absolute"}
                      zIndex={-3}
                    />
                  )}

                  {profileImg ? (
                    <Image
                      source={{
                        uri: profileImage,
                      }}
                      width={"54px"}
                      height={"54px"}
                      position={undefined}
                      borderRadius={"50%"}
                      alt={"Profile Image"}
                    />
                  ) : (
                    <>
                      {<Event height={"50"} width={"50"} color={"#32AE78"} />}
                    </>
                  )}
                </Box>

                <VStack px={2} py={1.5} justifyContent={"center"} space={1}>
                  <HStack justifyContent={"center"}>
                    <Tooltip
                      label={event?.title ? event?.title : event?.name}
                      openDelay={500}
                    >
                      <Pressable onPress={() => navigateToEvent(item)}>
                        <Text
                          fontSize={"18px"}
                          width={"200px"}
                          color={"AVColor.primaryText"}
                          numberOfLines={1}
                          text-overflow={"ellipsis"}
                          textAlign={"center"}
                          fontWeight={500}
                        >
                          {event?.title ? event?.title : event?.name}
                        </Text>

                        {(item?.type == "LeagueOwner") &&
                          item.subType !== "Class" && (
                            <Text
                              fontSize={"18px"}
                              width={"200px"}
                              color={"AVColor.primaryText"}
                              numberOfLines={1}
                              text-overflow={"ellipsis"}
                              textAlign={"center"}
                              fontWeight={500}
                            >
                              {event?.data
                                ? event?.data
                                : event?.season
                                  ? event?.season
                                  : event?.title}
                            </Text>
                          )}
                      </Pressable>
                    </Tooltip>
                  </HStack>

                  {event?.type == "Event" && (
                    <VStack>
                      <HStack
                        alignItems={"center"}
                        space={"15px"}
                        marginTop={"5px"}
                      >
                        <CalenderCardIcon
                          height={"24"}
                          width={"24"}
                          color={"black"}
                        />
                        <Text color={"black"} fontSize={"14px"}>
                          {Moment(item?.start).format("MM/DD/YYYY")}
                        </Text>
                      </HStack>
                      <HStack alignItems={"center"} space={"15px"}>
                        <TimerCardIcon
                          height={"24"}
                          width={"24"}
                          color={"black"}
                        />
                        <Text color={"black"} fontSize={"14px"}>
                          {Moment(item?.start).format("hh:mm A")} -{" "}
                          {Moment(item?.end).format("hh:mm A")}
                        </Text>
                      </HStack>
                      <HStack alignItems={"center"} space={"10px"}>
                        <HStack width={"30px"}>
                          <LocationCardIcon
                            height={"24"}
                            width={"24"}
                            color={"black"}
                          />
                        </HStack>
                        <Text
                          numberOfLines={1}
                          fontSize={"14px"}
                          color={"black"}
                        >
                          {item?.locURL
                            ? item?.locURL
                            : item.location
                              ? item.location?.title
                              : event?.location?.title
                                ? event?.location?.title
                                : event?.locURL
                                  ? event?.locURL
                                  : event?.url}
                        </Text>
                      </HStack>
                    </VStack>
                  )}
                </VStack>

                {event?.type == "Event" && (
                  <HStack
                    py={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                    backgroundColor={"rgba(0,167,188,1)"}
                    height={"25px"}
                  >
                    <Text
                      fontSize="14px"
                      color={"rgba(255,255,255,1)"}
                      fontWeight={500}
                      textAlign="center"
                    >
                      {event?.status === "Canceled"
                        ? Loc.currentLang.menu.canceled
                        : item?.status === "Canceled"
                          ? Loc.currentLang.menu.canceled
                          : item?.participants?.find(
                                (x) => x.id == currentUser.id,
                              )
                            ? (() => {
                                const myStatus = item?.participants?.find(
                                  (x) => x.id == currentUser.id,
                                )?.status;
                                if (myStatus === "Yes")
                                  return Loc.currentLang.menu.attending;
                                if (myStatus === "No")
                                  return Loc.currentLang.menu.notAttending;
                                if (myStatus === "Maybe")
                                  return Loc.currentLang.menu.maybe;
                                return Loc.currentLang.menu.rsvp;
                              })()
                            : participant?.status === "Yes"
                              ? Loc.currentLang.menu.attending
                              : participant?.status === "No"
                                ? Loc.currentLang.menu.notAttending
                                : participant?.status === "Maybe"
                                  ? Loc.currentLang.menu.maybe
                                  : Loc.currentLang.menu.rsvp}
                    </Text>
                  </HStack>
                )}
              </Pressable>
            );
          })}
        </HStack>
      </HStack>
    </VStack>
  );
};

export default React.memo(SeriesEvents);
